/**
 * A client's Dashboards tab: the Explore panel (this client's dashboards and
 * data sources, each with a "+") beside the open dashboard — or, with none
 * yet, the first-dashboard empty state.
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  reportsApi,
  type Attachment,
  type Catalogue,
  type Client,
  type ReportDocument,
} from '../reports/reports-api.ts';
import { RemovedDocuments } from '../reports/RemovedDocuments.tsx';
import { DashboardView } from './DashboardView.tsx';
import { ExploreTree } from './explore/ExploreTree.tsx';
import { Ga4Explore } from './explore/Ga4Explore.tsx';
import { go } from './routes.ts';

export function ClientDashboards(props: {
  organisationId: string;
  client: Client;
  catalogue: Catalogue;
  documents: ReportDocument[];
  openId: string | null;
  /** An Explore view to show instead of a dashboard (GA4). */
  explore?: string | undefined;
  onCreate: () => void;
  onEdit: (loaded: Parameters<Parameters<typeof DashboardView>[0]['onEdit']>[0]) => void;
  onChanged: () => void;
}): ReactNode {
  const dashboards = props.documents.filter((document) => document.kind === 'DASHBOARD');
  const [sources, setSources] = useState<Attachment[]>([]);
  useEffect(() => {
    reportsApi
      .sources(props.organisationId, props.client.id)
      .then(setSources)
      .catch(() => setSources([]));
  }, [props.organisationId, props.client.id]);

  const openId = props.openId ?? dashboards[0]?.id ?? null;
  const sourceLabel = (key: string) =>
    props.catalogue.sources.find((source) => source.key === key)?.label ?? key;
  const toData = () =>
    go({ area: 'client', clientId: props.client.id, tab: 'data', documentId: null });

  return (
    <div className="ag-explore-layout">
      <aside className="ag-explore" aria-label="Explore">
        <h2>Explore</h2>
        <div className="ag-explore-group">
          <div className="ag-explore-head">
            <span>Dashboards</span>
            <button
              type="button"
              className="ag-plus"
              aria-label="New dashboard"
              onClick={props.onCreate}
            >
              +
            </button>
          </div>
          <ul>
            {dashboards.map((document) => (
              <li key={document.id}>
                <a
                  className={`ag-explore-link${document.id === openId && !props.explore ? ' on' : ''}`}
                  href={`#/client/${props.client.id}/dashboards/${document.id}`}
                  aria-current={document.id === openId && !props.explore ? 'page' : undefined}
                >
                  {document.name}
                </a>
              </li>
            ))}
            {dashboards.length === 0 && <li className="ag-explore-none">None yet</li>}
          </ul>
        </div>
        <div className="ag-explore-group">
          <div className="ag-explore-head">
            <span>Data Sources</span>
            <button
              type="button"
              className="ag-plus"
              aria-label="Connect a data source"
              onClick={toData}
            >
              +
            </button>
          </div>
          <ul>
            {sources.map((source) => (
              <li key={source.id}>
                <button type="button" className="ag-explore-link" onClick={toData}>
                  {sourceLabel(source.source)}
                  {source.label && <small>{source.label}</small>}
                </button>
                {source.source === 'ga4' && (
                  <ExploreTree clientId={props.client.id} activeView={props.explore ?? null} />
                )}
              </li>
            ))}
            {sources.length === 0 && <li className="ag-explore-none">None connected</li>}
          </ul>
        </div>
        <RemovedDocuments
          organisationId={props.organisationId}
          clientId={props.client.id}
          show="dashboards"
          refreshKey={props.documents}
          onRestored={props.onChanged}
        />
      </aside>
      <div className="ag-explore-main">
        {props.explore ? (
          <Ga4Explore
            organisationId={props.organisationId}
            client={props.client}
            catalogue={props.catalogue}
            viewSlug={props.explore}
            onReportCreated={props.onChanged}
            property={sources.find((source) => source.source === 'ga4')?.label ?? null}
          />
        ) : openId ? (
          <DashboardView
            organisationId={props.organisationId}
            documentId={openId}
            catalogue={props.catalogue}
            client={props.client}
            onEdit={props.onEdit}
            onDelete={() =>
              reportsApi.remove(props.organisationId, openId).then(() => {
                go({
                  area: 'client',
                  clientId: props.client.id,
                  tab: 'dashboards',
                  documentId: null,
                });
                props.onChanged();
              })
            }
          />
        ) : (
          <div className="ag-feature">
            <h2>Build your first dashboard</h2>
            <p className="ag-feature-lead">
              Connect an account your agency already owns, or start from a template that fills with
              sample numbers right away.
            </p>
            <ul>
              <li>Connect a source and get a ready-made dashboard</li>
              <li>Or compose your own with the drag-and-drop builder</li>
              <li>Share live dashboards with your client in their portal</li>
            </ul>
            <div className="ag-feature-actions">
              <button type="button" className="btn" onClick={toData}>
                Connect Data Source
              </button>
              <button type="button" className="btn secondary" onClick={props.onCreate}>
                Create Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
